import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Collections_Enum,
  EmailTemplate,
  TemplateSource,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/email-template/v1/emailtemplate.pb';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { useQueryClient } from 'react-query';
import { EmailTemplateQueries } from '@frontend/api-email-template';
import { usePreviewModal } from '@frontend/email-preview-modal';
import { invalidateEmailTemplateLists, useEmailTemplates } from '@frontend/email-templates';
import { useAppScopeStore } from '@frontend/scope';
import { useDebouncedValue } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import { type TemplateFilter } from '../components/bulk-email-compose-view/constants';
import { useBulkNavigator } from './use-bulk-navigator';

export const useComposeTemplates = () => {
  const { selectedOrgId: orgId, selectedLocationIds: appSelectedLocationIds } = useAppScopeStore();
  const { locationIds: contextLocationIds } = useBulkMessageEditorContext();
  const navigateTo = useBulkNavigator(CampaignType.EMAIL);
  const { createTemplate, deleteTemplate, fetchOrgTemplate, fetchSystemTemplate } = useEmailTemplates();
  const queryClient = useQueryClient();

  const selectedLocationIds = contextLocationIds.length ? contextLocationIds : appSelectedLocationIds;

  const [activeFilter, setActiveFilter] = useState<TemplateFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  /** Count of in-flight template operations (fetch for open/preview); when positive, the compose view shows a loading overlay. */
  const [pendingTemplateActions, setPendingTemplateActions] = useState(0);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | undefined>();

  const endTemplateAction = useCallback(() => {
    setPendingTemplateActions((n) => Math.max(0, n - 1));
  }, []);

  const debouncedSearchQuery = useDebouncedValue(searchQuery, 400);

  const listAllEmailTemplatesRequest = useMemo(
    () => ({
      orgId,
      locationIds: selectedLocationIds,
      collection: Collections_Enum.BULK_MESSAGING,
      searchFilter: debouncedSearchQuery || undefined,
      ...(activeFilter === 'custom'
        ? { sources: [TemplateSource.ORG] }
        : activeFilter === 'weave'
        ? { sources: [TemplateSource.SYSTEM] }
        : {}),
    }),
    [activeFilter, debouncedSearchQuery, orgId, selectedLocationIds]
  );

  const { data, fetchNextPage, hasNextPage, isFetched, isFetchingNextPage } =
    EmailTemplateQueries.useListAllEmailTemplates(listAllEmailTemplatesRequest);

  const visibleTemplates = useMemo(() => {
    const pages = data?.pages ?? [];
    const seen = new Set<string>();
    return pages
      .flatMap((page) => page?.templates ?? [])
      .filter((template) => {
        if (!template.id || seen.has(template.id)) return false;
        seen.add(template.id);
        return true;
      });
  }, [data]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleCustomTemplateClick = useCallback(
    async (isEditingTemplate?: boolean, templateId?: string) => {
      if (!templateId) return;
      setPendingTemplateActions((n) => n + 1);
      try {
        const fetched = await fetchOrgTemplate(templateId);
        if (!fetched) return;
        navigateTo.composer({ templateId, isEditingTemplate });
      } finally {
        endTemplateAction();
      }
    },
    [endTemplateAction, fetchOrgTemplate, navigateTo]
  );

  const handleWeaveTemplateClick = useCallback(
    async (templateId?: string) => {
      if (!templateId) return;
      setPendingTemplateActions((n) => n + 1);
      try {
        await fetchSystemTemplate(templateId);
        navigateTo.composer({ isSystemTemplate: true, templateId });
      } finally {
        endTemplateAction();
      }
    },
    [endTemplateAction, fetchSystemTemplate, navigateTo]
  );

  const handleDelete = useCallback(
    (templateId: string) => {
      setIsDeleting(true);
      deleteTemplate(
        { templateId },
        {
          onSettled: () => {
            setIsDeleting(false);
          },
        }
      );
    },
    [deleteTemplate]
  );

  const handleDuplicate = useCallback(
    async (template: EmailTemplate) => {
      if (!template.id) return;
      setIsDuplicating(true);
      try {
        const fetched = await fetchOrgTemplate(template.id);
        if (!fetched) return;

        await createTemplate({
          orgId,
          locationIds: fetched.locationIds?.length ? fetched.locationIds : selectedLocationIds,
          title: `Copy of ${fetched.title ?? template.title ?? ''}`,
          subject: fetched.subject,
          templateHtml: fetched.templateHtml,
          templateJson: fetched.templateJson,
          collection: Collections_Enum.BULK_MESSAGING,
        });

        invalidateEmailTemplateLists(queryClient);
      } finally {
        setIsDuplicating(false);
      }
    },
    [createTemplate, fetchOrgTemplate, orgId, queryClient, selectedLocationIds]
  );

  const [previewHtml, setPreviewHtml] = useState<string>();
  const { deviceSizeProps, generatePreview, previewModalProps, transformedText } = usePreviewModal(
    previewHtml,
    TemplateType_Slug.BULK_MESSAGE
  );

  useEffect(() => {
    if (transformedText) generatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedText]);

  useEffect(() => {
    if (!previewModalProps.show) {
      setPreviewHtml(undefined);
      setPreviewTemplate(undefined);
    }
  }, [previewModalProps.show]);

  const handleView = useCallback(
    async (template: EmailTemplate) => {
      let html = template.templateHtml;
      if (!html) {
        setPendingTemplateActions((n) => n + 1);
        try {
          const fetched = EmailTemplateQueries.isSystemEmailTemplate(template)
            ? await fetchSystemTemplate(template.id)
            : await fetchOrgTemplate(template.id);
          html = fetched?.templateHtml;
        } finally {
          endTemplateAction();
        }
      }
      if (html) {
        setPreviewHtml(html);
        setPreviewTemplate(template);
      }
    },
    [endTemplateAction, fetchOrgTemplate, fetchSystemTemplate]
  );

  return {
    activeFilter,
    deviceSizeProps,
    handleCustomTemplateClick,
    handleDelete,
    handleDuplicate,
    handleView,
    handleWeaveTemplateClick,
    hasMore: hasNextPage,
    isDeleting,
    isDuplicating,
    isFetchingMore: isFetchingNextPage,
    isLoading: !isFetched,
    isTemplateActionLoading: pendingTemplateActions > 0,
    loadMore,
    previewHtml,
    previewModalProps,
    previewTemplate,
    searchQuery,
    setActiveFilter,
    setPreviewHtml,
    setSearchQuery,
    transformedText,
    visibleTemplates,
  };
};
